import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { Logout, Save } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

interface CompanyInfo {
  companyName: string;
  address: string;
  email: string;
  phone: string;
  latitude: number;
  longitude: number;
}

export const CompanyInfoEditor: React.FC = () => {
  const { logout } = useAuth();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    companyName: '씨유미디어그룹',
    address: '서울특별시 영등포구 양평동3가 16, 612호',
    email: 'che37412@hanmail.net',
    phone: '02-2088-7388',
    latitude: 37.525791,
    longitude: 126.88667,
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    setLoading(true);
    try {
      const sidebarApi = await import('../services/api').then(m => m.sidebarApi);

      // 사이드바 데이터를 가져와서 사용
      const sidebarData = await sidebarApi.getSidebarData();
      if (sidebarData) {
        setCompanyInfo({
          companyName: sidebarData.companyName || companyInfo.companyName,
          address: sidebarData.address || companyInfo.address,
          email: sidebarData.email || companyInfo.email,
          phone: sidebarData.phone || companyInfo.phone,
          latitude: sidebarData.latitude || companyInfo.latitude,
          longitude: sidebarData.longitude || companyInfo.longitude,
        });
      }

    } catch (error) {
      console.error('회사 정보 로드 실패:', error);
      setErrorMessage('회사 정보를 불러오는데 실패했습니다.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CompanyInfo, value: string | number) => {
    setCompanyInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sidebarApi = await import('../services/api').then(m => m.sidebarApi);

      // 사이드바 데이터 저장
      await sidebarApi.saveSidebarData({
        companyName: companyInfo.companyName,
        address: companyInfo.address,
        email: companyInfo.email,
        phone: companyInfo.phone,
        latitude: companyInfo.latitude,
        longitude: companyInfo.longitude,
        contactTitle: '문의하기', // 기존 값 유지
      });

      setShowSuccess(true);
    } catch (error) {
      console.error('저장 실패:', error);
      setErrorMessage('저장에 실패했습니다.');
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('로그아웃 실패:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          회사 정보를 불러오는 중...
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            회사 정보 관리
          </Typography>
          <Button
            color="inherit"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            저장
          </Button>
          <IconButton
            color="inherit"
            onClick={handleLogout}
            sx={{ ml: 1 }}
          >
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          회사 정보 관리
        </Typography>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="회사명"
                value={companyInfo.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="주소"
                value={companyInfo.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="이메일"
                type="email"
                value={companyInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="전화번호"
                value={companyInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="위도"
                type="number"
                value={companyInfo.latitude}
                onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="경도"
                type="number"
                value={companyInfo.longitude}
                onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </Paper>

        <Snackbar
          open={showSuccess}
          autoHideDuration={6000}
          onClose={() => setShowSuccess(false)}
        >
          <Alert onClose={() => setShowSuccess(false)} severity="success">
            회사 정보가 성공적으로 저장되었습니다!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={() => setShowError(false)}
        >
          <Alert onClose={() => setShowError(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}; 